import React, { useEffect, useState, useMemo, useRef } from 'react';
import styled from 'styled-components';

// Components
import { FormGroup, Row } from 'react-bootstrap';
import { FormattedMessage } from 'react-intl';
import TheWorkNumber from '../../../components/TheWorkNumber';
import WaitlistInfoSection from '../../../components/WaitlistInfoSection';
import { ApplicationChecklistCompletionStatus } from './ApplicationChecklistCompletionStatus';
import ApplicationNotes from './ApplicationNotes';
import ApplicationReceived from './ApplicationReceived';
import CreditCriminalStatus from './CreditCriminalStatus';
import ManageAffordableWaitlistModals from '../../ManageAffordableWaitlist/ManageAffordableWaitlistModals';
import WaitlistV2StatusActionModals from './WaitlistV2StatusActionModals';
import ApplicationChecklist from './ApplicationChecklist';
import { Typography } from '@fortress-technology-solutions/fortress-component-library/Atoms';
import { Grid } from '@fortress-technology-solutions/fortress-component-library/Molecules';
import { Form } from '@fortress-technology-solutions/fortress-component-library/Organisms';
import { reduxForm } from 'redux-form';
import HouseholdApplicationsDrawer from '../../../components/HouseholdApplicationsDrawer';
import LeaseApplicationStatusTracker from '../../../components/LeaseApplicationStatusTracker';

// Hooks
import { useOneProperty } from '../../../hooks/data-fetching/useOneProperty';
import useWaitlistService from '../../../components/WaitlistInfoSection/hooks';
import { useApplicationStatusOptions } from '../hooks';
import {
  useFetchAffordableWaitlist,
  useProviderPropertyProfiles,
} from './hooks.queries';
import useModalProps from '../../../hooks/useModalProps';
import useApplicationStatusSingleSelect from './useApplicationStatusSingleSelect';
import useAffordableWaitlistValidations from '../../ManageAffordableWaitlist/hooks.useAffordableWaitlistValidations';
import useWaitlistApplicationWindow from '../../../hooks/useWaitlistApplicationWindow';
import { useFlags } from 'launchdarkly-react-client-sdk';

// Types
import type { Applicant, Application } from '../../CreateApplication/types';
import type { FeatureFlags } from '../../../types';
import type { DropdownOption, User } from '../../App/types';
import type { DecisionStatus } from '../types';
import type { AddToWaitlistParams } from '../../ManageAffordableWaitlist/types';

// Utils
import { filter, pathOr } from 'ramda';
import {
  showWaitlistUpdateFail,
  showWaitlistUpdateSuccess,
} from '../../../components/WaitlistInfoSection/utils';
import { getSentAndExecutedStatus } from '../../../utils/lease-helpers';
import { formatNBedsArr } from '../../../utils/prospectPreferences-helpers';
import { formatPhoneNumber } from '@fortress-technology-solutions/fortress-component-library/utils';

// Constants
import messages from './messages';
import CreditCriminalStatusScreening from '../../../components/CreditCriminalStatusScreening';
import { APPLICATION_STATUS_IDS } from '../constants';

const RowWarning = styled(Row)`
  .highlight-details {
    padding: 10px;
    margin: 0 20px 20px;
  }
`;

type Props = {
  intl: Object,
  propertyId: string,
  organizationId: string,
  currentRecord: any,
  applicationDecisionStatus: Array<DecisionStatus>,
  applicationStatuses: Array<DropdownOption>,
  applicationId: string,
  floorPlans: Array<Object>,
  screening: Object,
  screeningLetterTypes: Array<Object>,
  saveScreeningResults: Function,
  generateAdverseActionLetter: Function,
  checkUserPermissions: Function,
  currentUser: User,
  handleNotesChange: Function,
  handleChecklistOptionChange: Function,
  handleEditApplicationForm: Function,
  handleStatusChange: Function,
  submitScreeningApplication: Function,
  promptToaster: Function,
  selectedProperty: Object,
  householdId: string,
  initialValues: { applicationStatusId: string },
  handleMultipleChecklistOptionsChange: Function,
  onResetSuccess?: Function,
};

type LeaseProps = {
  intl: Object,
  currentRecord: Application,
  handleStatusChange?: Function,
  handleNotesChange?: Function,
  applicationStatuses: Array<DropdownOption>,
  checkUserPermissions: Function,
  isResident?: boolean,
  leaseExecuted?: boolean,
  leaseSentToPortal?: boolean,
  applicationStatusId: string,
  modalProps: Object, // AddToWaitlistModalProps
  propertyDetails?: Object,
  refreshApplication?: () => void,
  floorPlans: Array<Object>,
};

export const getWaitlistSectionProps = (currentRecord) => {
  const {
    applicationStatus: currentApplicationStatus,
    property: { organizationId },
    propertyId,
    id: applicationId,
  } = currentRecord;
  return {
    organizationId,
    propertyId,
    applicationId,
    currentApplicationStatus,
  };
};

export const statusOptionIsWaitlist = (status) =>
  /Waitlist/gi.test(status?.text ?? '');

export const getWaitlistStatusId = (applicationStatuses) =>
  applicationStatuses.find(statusOptionIsWaitlist)?.value ?? '';

export const NEW_PORTAL_APP_STATUS = 'New Portal App';

// APPLICATION_STATUS_IDS only hardcodes canceled and denied, so a status is identified by the
// text on its dropdown option, the way getWaitlistStatusId already does it.
export const getStatusName = (applicationStatuses, statusId) =>
  applicationStatuses?.find((status) => status.value === statusId)?.text ?? '';

export const applicationStatusChangeHandler = async (
  // applicationStatus Props
  currentStatusId,
  newStatusId,
  waitlistStatusId,
  localWaitlistInfo,
  waitlistActions,
  handleStatusChange,
  // Modal Props
  modalProps,
  addToWaitlistParams,
  cancelApplicationParams,
  denyApplicationParams,
  // Other Props
  intl,
  affordableWaitlistEnabled = false,
): Promise<Void> => {
  const updateSingleSelectValue = (applicationStatusId) => {
    addToWaitlistParams.setValue('applicationStatusId', applicationStatusId);
  };

  if (newStatusId === currentStatusId) {
    updateSingleSelectValue(currentStatusId);
    return;
  }

  const { updateWaitlist } = waitlistActions;
  const { id: applicationId } = localWaitlistInfo;

  const executeUpdates = async (
    currentApplicationId,
    waitlistDateRange,
    callBE = true,
  ) => {
    handleStatusChange(null, newStatusId);
    const applicationIdToBeSent =
      currentApplicationId || addToWaitlistParams?.applicationIdExt;
    await updateWaitlist(applicationIdToBeSent, waitlistDateRange, callBE);
    affordableWaitlistEnabled && showWaitlistUpdateSuccess(intl);
  };

  if (
    newStatusId === APPLICATION_STATUS_IDS.canceled &&
    affordableWaitlistEnabled
  ) {
    if (cancelApplicationParams.showCancelModal) {
      modalProps.handleOpen({
        modalType: 'cancel',
        applicationId,
        shouldRefetchWaitlist: false,
        affordableWaitlistRow: {
          applicationId,
        },
        updateCallback: () => {
          updateSingleSelectValue(newStatusId);
          handleStatusChange(null, newStatusId);
        },
      });
    }
    return;
  }

  if (
    newStatusId === APPLICATION_STATUS_IDS.denied &&
    affordableWaitlistEnabled
  ) {
    if (denyApplicationParams.showDenyModal) {
      modalProps.handleOpen({
        modalType: 'deny',
        applicationId,
        shouldRefetchWaitlist: false,
        affordableWaitlistRow: {
          applicationId,
          applicationStatusId: currentStatusId,
        },
        updateCallback: () => {
          updateSingleSelectValue(newStatusId);
          handleStatusChange(null, newStatusId);
        },
      });
    }
    return;
  }

  const cancelWaitlistUpdate = () => updateSingleSelectValue(currentStatusId);

  if (currentStatusId === waitlistStatusId) {
    const waitlistDateRange = {
      waitlistStartTime: localWaitlistInfo.waitlistStartTime,
      waitlistEndTime: new Date(),
    };
    await executeUpdates(applicationId, waitlistDateRange);
    return;
  }
  if (newStatusId === waitlistStatusId) {
    const waitlistDateRange = {
      waitlistStartTime: localWaitlistInfo?.waitlistStartTime ?? new Date(),
      waitlistEndTime: null,
    };
    if (addToWaitlistParams.showAddToWaitlistModal) {
      addToWaitlistParams.waitlistDateRange = waitlistDateRange;
      if (addToWaitlistParams.showReinstateModal) {
        modalProps.handleOpen({
          modalType: 'reinstate',
          affordableWaitlistId: addToWaitlistParams.affWlRecord.id,
          affordableWaitlistRow: {
            applicationId,
            affordableProgram: null,
            incomeLevel: null,
            anticipatedAnnualIncome: null,
            requestingAccommodation: null,
            preferences: null,
          },
          updateCallback: executeUpdates,
          updateCallbackParams: {
            applicationId,
            waitlistDateRange,
            callBE: false, // Don't need to update the waitlist twice
          },
          cancelCallback: cancelWaitlistUpdate,
        });
      } else {
        modalProps.handleOpen({
          modalType: 'addToWaitlist',
          addToWaitlistParams,
          updateCallback: executeUpdates,
          updateCallbackParams: {
            applicationId,
            waitlistDateRange,
            callBE: false, // Don't need to update the waitlist twice
          },
          cancelCallback: cancelWaitlistUpdate,
        });
      }
    } else {
      await executeUpdates(applicationId, waitlistDateRange);
    }

    return;
  }

  updateSingleSelectValue(newStatusId);
  handleStatusChange(null, newStatusId);
};

export const LeaseApplication = ({
  intl,
  currentRecord,
  handleStatusChange,
  handleNotesChange,
  applicationStatuses,
  checkUserPermissions,
  isResident,
  leaseExecuted,
  leaseSentToPortal,
  applicationStatusId,
  headOfHouseholdId,
  applicationId: applicationIdExt,
  floorPlans,
  modalProps,
  affWlRecord,
  propertyDetails,
  refreshApplication,
}: LeaseProps) => {
  const statusUpdateAllowed = checkUserPermissions([
    'application-status-update',
    'application-status-update-excluding-approved',
  ]);
  const prospectUpdateAllowed = checkUserPermissions(['prospect-update']);

  // Affordable Waitlist Related ----------------
  const {
    affordableWaitlistFlag,
    hasAffordableWaitlistPermissionAdd,
    hasAffordableWaitlistPermissionReinstate,
    accountThisPropertyForWaitlist,
    affordableWaitlistPropertySetting,
    isAffordableWaitlistPropertySettingStatusClosed,
    propertyAffordablePrograms,
  } = useAffordableWaitlistValidations();
  const { isEntryBlocked, isV2WaitlistEnabled } =
    useWaitlistApplicationWindow();

  const [pendingWaitlistV2Action, setPendingWaitlistV2Action] = useState(null);

  // A modal's success callback can fire from a stale render closure, so the
  // pending action is mirrored in a ref and read from there when deciding
  // whether an arriving success still belongs to the open action.
  const pendingWaitlistV2ActionRef = useRef(null);

  const startWaitlistV2Action = (action) => {
    pendingWaitlistV2ActionRef.current = action;
    setPendingWaitlistV2Action(action);
  };

  const clearWaitlistV2Action = () => {
    pendingWaitlistV2ActionRef.current = null;
    setPendingWaitlistV2Action(null);
  };

  const showAddToWaitlistModal =
    affordableWaitlistFlag &&
    accountThisPropertyForWaitlist &&
    affordableWaitlistPropertySetting &&
    hasAffordableWaitlistPermissionAdd;
  const showReinstateModal =
    affWlRecord && affWlRecord.affordableWaitlistHistory?.length > 0;

  const showWaitlistForm =
    !affordableWaitlistFlag || !propertyDetails?.setup?.affordableWaitlist;

  const affordableWaitlistEnabled =
    affordableWaitlistFlag &&
    accountThisPropertyForWaitlist &&
    affordableWaitlistPropertySetting;
  // --------------------------------------------

  const disableStatusChange =
    !statusUpdateAllowed || isResident || leaseExecuted || leaseSentToPortal;

  const {
    property: { id: propertyId, organizationId },
    id: applicationId,
    applicationStatus: { id: currentStatusId },
    applicants,
    prospectInfo: {
      prospectPreferences: { nBedsArr, nBeds, prefersUnitAccessible },
    },
  } = currentRecord;

  const waitlistStatusId = getWaitlistStatusId(applicationStatuses);
  const currentStatusName = getStatusName(applicationStatuses, currentStatusId);

  // Picking Waitlist is a V2 wizard route (the add and reinstate branches of
  // handleApplicationStatusChange) whenever V2 is enabled and the application
  // is in a state the wizard accepts. On that route the V2 gate decides the
  // option's state, so it matches what selecting it does. Every other route is
  // the legacy one, which the backend refuses on a closed waitlist whatever
  // its dates.
  const waitlistOpensV2Wizard =
    isV2WaitlistEnabled &&
    (affWlRecord?.id
      ? Boolean(showReinstateModal)
      : currentStatusName === NEW_PORTAL_APP_STATUS);
  const isWaitlistEntryBlocked = waitlistOpensV2Wizard
    ? isEntryBlocked
    : isAffordableWaitlistPropertySettingStatusClosed;

  const disableWaitlistOption = waitlistOpensV2Wizard
    ? !hasAffordableWaitlistPermissionAdd || isEntryBlocked
    : affordableWaitlistFlag &&
      accountThisPropertyForWaitlist &&
      affordableWaitlistPropertySetting &&
      (!hasAffordableWaitlistPermissionAdd || isWaitlistEntryBlocked);

  const [localWaitlistInfo, waitlistActions, isLoading] = useWaitlistService(
    organizationId,
    propertyId,
    applicationId,
  );

  const waitlistSectionProps = !showWaitlistForm
    ? {}
    : {
        ...getWaitlistSectionProps(currentRecord),
        localWaitlistInfo,
        waitlistActions,
        isLoading,
      };

  const handleApplicationStatusChange = async (
    event,
    newStatusId,
    addToWaitlistParams,
    cancelApplicationParams,
    denyApplicationParams,
  ) => {
    if (isV2WaitlistEnabled && newStatusId !== currentStatusId) {
      if (
        newStatusId === APPLICATION_STATUS_IDS.canceled &&
        hasAffordableWaitlistPermissionAdd &&
        affWlRecord?.id
      ) {
        startWaitlistV2Action('cancel');
        return;
      }
      if (
        newStatusId === APPLICATION_STATUS_IDS.denied &&
        hasAffordableWaitlistPermissionAdd &&
        affWlRecord?.id
      ) {
        startWaitlistV2Action('deny');
        return;
      }
      if (
        newStatusId === waitlistStatusId &&
        hasAffordableWaitlistPermissionAdd &&
        addToWaitlistParams.showReinstateModal &&
        affWlRecord?.id
      ) {
        startWaitlistV2Action('reinstate');
        return;
      }
      // The mirror image of reinstate: a New Portal App household submitted a portal application,
      // so the Application exists and the prospect is already converted, but it has never had a
      // waitlist record. Gating on the status as well as the missing record keeps this condition
      // inside the endpoint's own guard, so no wizard opens that the backend will refuse — a
      // Denied or Canceled application with no record stays on the legacy path. The endpoint also
      // accepts a Waitlist-status household (ENG-32458), which this dropdown cannot reach: from
      // Waitlist the only transitions offered are Canceled, Denied and Waitlist itself. That case
      // is the Waitlist Summary card's Add button.
      if (
        newStatusId === waitlistStatusId &&
        hasAffordableWaitlistPermissionAdd &&
        !affWlRecord?.id &&
        currentStatusName === NEW_PORTAL_APP_STATUS
      ) {
        startWaitlistV2Action('add');
        return;
      }
    }
    try {
      await applicationStatusChangeHandler(
        currentStatusId,
        newStatusId,
        waitlistStatusId,
        localWaitlistInfo,
        waitlistActions,
        handleStatusChange,
        modalProps,
        addToWaitlistParams,
        cancelApplicationParams,
        denyApplicationParams,
        intl,
        affordableWaitlistEnabled,
      );
    } catch (err) {
      showWaitlistUpdateFail(intl, err);
    }
  };

  const permissionRequired =
    (waitlistOpensV2Wizard ||
      (affordableWaitlistFlag && accountThisPropertyForWaitlist)) &&
    (!statusUpdateAllowed || !hasAffordableWaitlistPermissionAdd);
  const waitlistIsClosed =
    (waitlistOpensV2Wizard ||
      (affordableWaitlistFlag && accountThisPropertyForWaitlist)) &&
    isWaitlistEntryBlocked;

  const waitlistTooltipText = permissionRequired
    ? intl.formatMessage(messages.permissionRequired)
    : waitlistIsClosed
    ? intl.formatMessage(messages.waitlistClosed)
    : '';

  const [applicationStatusOptions] = useApplicationStatusOptions(
    applicationStatuses,
    applicationStatusId,
    disableWaitlistOption,
    affordableWaitlistEnabled,
    waitlistTooltipText,
    hasAffordableWaitlistPermissionReinstate,
  );

  const hohApplicant = useMemo(() => {
    return applicants.length === 1
      ? applicants[0]
      : applicants.find(
          (applicant) =>
            applicant.affordableRelationshipId === headOfHouseholdId ||
            applicant.applicantType?.name === 'Financial Lease Signer',
        );
  }, [applicants, headOfHouseholdId]);

  const addToWaitlistParams: AddToWaitlistParams = {
    info: {
      applicant: hohApplicant?.name,
      phone: formatPhoneNumber(hohApplicant?.applicantCustomer?.phoneNumber),
      incomeLevel: 'Not Determined',
      householdSize: applicants.length,
      preferredBedroomSize: formatNBedsArr(nBedsArr, nBeds),
      accessibleUnit: prefersUnitAccessible ? 'Yes' : 'No',
    },
    affordablePrograms: propertyAffordablePrograms,
    showAddToWaitlistModal,
    showReinstateModal,
    applicationIdExt,
    affWlRecord,
  };

  const cancelApplicationParams = {
    showCancelModal: showAddToWaitlistModal,
  };

  const denyApplicationParams = {
    showDenyModal: showAddToWaitlistModal,
  };

  const { ReactHookFormProps, formProps, setValue } =
    useApplicationStatusSingleSelect({
      handleApplicationStatusChange,
      statusUpdateAllowed,
      disableStatusChange,
      disableWaitlistOption,
      addToWaitlistParams,
      applicationStatusOptions,
      applicationStatusId,
      currentStatusId,
      affordableWaitlistPropertySetting,
      cancelApplicationParams,
      denyApplicationParams,
    });
  addToWaitlistParams.setValue = setValue;

  const handleWaitlistV2ActionClose = () => {
    clearWaitlistV2Action();
    addToWaitlistParams.setValue('applicationStatusId', currentStatusId);
  };

  const handleWaitlistV2ActionSuccess = (result) => {
    // A dismissed modal keeps its mutation alive, so a success can arrive for an
    // action the user already replaced (deny submitted, dismissed, cancel
    // opened, deny resolves). Only accept the action that is still pending.
    if (result?.action !== pendingWaitlistV2ActionRef.current) return;

    clearWaitlistV2Action();

    // Cancel + "continue application" only ends the waitlist record; the
    // application deliberately stays in process, so leave its status alone and
    // put the dropdown back where it was.
    if (result.keptInProcess) {
      addToWaitlistParams.setValue('applicationStatusId', currentStatusId);
      return;
    }

    // The backend has already moved the application to In Process, and may
    // have changed its property class, so writing the status back from the
    // record held here would PUT a stale application. Reload it instead.
    if (result.action === 'add' && result.movedToInProcess) {
      addToWaitlistParams.setValue('applicationStatusId', currentStatusId);
      refreshApplication?.();
      return;
    }

    const newStatusIdByAction = {
      deny: APPLICATION_STATUS_IDS.denied,
      cancel: APPLICATION_STATUS_IDS.canceled,
      reinstate: waitlistStatusId,
      add: waitlistStatusId,
    };
    const newStatusId = newStatusIdByAction[result.action];
    addToWaitlistParams.setValue('applicationStatusId', newStatusId);
    handleStatusChange(null, newStatusId);
  };

  return (
    <div className="container-fluid ledger-head">
      <Grid container spacing={2}>
        <Grid item xs={12} md={10}>
          <Typography variant="headlineMedium" component="h1">
            {intl.formatMessage(messages.leaseApplication)}
          </Typography>
        </Grid>
        <Grid item xs={12} md={2}>
          <Form ReactHookFormProps={ReactHookFormProps} {...formProps} />
        </Grid>
      </Grid>

      {showWaitlistForm && (
        <FormGroup>
          <WaitlistInfoSection {...waitlistSectionProps} />
        </FormGroup>
      )}

      <ApplicationReceived currentRecord={currentRecord} />

      <ApplicationNotes
        intl={intl}
        currentRecord={currentRecord}
        handleNotesChange={handleNotesChange}
        maxLength={620}
        isResident={isResident}
        prospectUpdateAllowed={prospectUpdateAllowed}
      />
      {showAddToWaitlistModal && (
        <ManageAffordableWaitlistModals
          applicationStatusId={applicationStatusId}
          organizationId={organizationId}
          propertyId={propertyId}
          applicationId={applicationId}
          intl={intl}
          {...modalProps}
        />
      )}
      {isV2WaitlistEnabled && (
        <WaitlistV2StatusActionModals
          organizationId={organizationId}
          propertyId={propertyId}
          affordableWaitlistId={affWlRecord?.id ?? null}
          applicationId={applicationId}
          floorPlans={floorPlans}
          pendingAction={pendingWaitlistV2Action}
          onClose={handleWaitlistV2ActionClose}
          onSuccess={handleWaitlistV2ActionSuccess}
        />
      )}
    </div>
  );
};

export const filterApplicants = (applicants: Array<Applicant>) => {
  const hasChecklist = (x) => x.checklist !== null;
  return filter(hasChecklist, applicants);
};

export const countCompleteApplications = (applicants: Array<Applicant>) => {
  const checklistIsApproved = (x) => x.checklist.approved;
  return filter(checklistIsApproved, applicants).length;
};

export const LeaseApplicationTab = ({
  intl,
  currentRecord,
  applicationDecisionStatus,
  applicationStatuses,
  handleChecklistOptionChange,
  handleEditApplicationForm,
  handleNotesChange,
  handleStatusChange,
  applicationId,
  screening,
  screeningLetterTypes,
  submitScreeningApplication,
  refreshActivityTable,
  saveScreeningResults,
  generateAdverseActionLetter,
  checkUserPermissions,
  currentUser,
  promptToaster,
  selectedProperty,
  householdId,
  initialValues: { applicationStatusId },
  affordableRelationships,
  handleMultipleChecklistOptionsChange,
  applicationUpdating,
  onResetSuccess,
  floorPlans,
}: Props) => {
  const [headOfHouseholdId, setHeadOfHouseholdId] = useState('');
  // ref: src/containers/ManageAffordableWaitlist/types.js
  const propertyId = selectedProperty?.id ?? '';
  const organizationId = currentUser?.user?.organization?.id ?? '';
  const { data: affWlRecord, refetch: updateAffWlRecord } =
    useFetchAffordableWaitlist(organizationId, propertyId, applicationId);

  useEffect(() => {
    setHeadOfHouseholdId(
      affordableRelationships?.filter(
        (relationship) => relationship?.name === 'Head of Household',
      )[0]?.id,
    );
  }, [affordableRelationships]);

  const propertyDetails = useOneProperty(organizationId, propertyId);
  const isTransUnionActive = propertyDetails[0]?.isTransUnionActive;
  const lease = pathOr({}, ['au', 'lease'], currentRecord);
  const { leaseExecuted, leaseSentToPortal } = getSentAndExecutedStatus(lease);
  const {
    theWorkNumber,
    transunionOffLeaseAppOff,
    applicationChecklistUpdate,
    leaseAppRevampTransunion,
    leaseAppRevampTruework,
    leaseAppRevampFullTab,
    affordaPortaV1,
  }: FeatureFlags = useFlags();

  const isNewScreeningOn = leaseAppRevampTransunion || leaseAppRevampTruework;

  const modalProps = useModalProps();

  const theWorkNumberProps = {
    organizationId: currentUser?.user?.organization?.id ?? '',
    propertyId: selectedProperty?.id ?? '',
    householdId: householdId ?? '',
    currentUser,
  };

  const adverseActionDeadlineDays =
    selectedProperty?.config?.adverseActionDeadlineDays;

  const applicationStatus = applicationStatuses?.find(
    (status) => status.value === applicationStatusId,
  );

  const isApplicationCanceledOrDenied =
    applicationStatus?.text === 'Canceled' ||
    applicationStatus?.text === 'Denied';

  const statusChangeHandler = (event: any, newStatusId: string) => {
    handleStatusChange(event, newStatusId);
    updateAffWlRecord();
  };

  const refreshApplication = () => {
    onResetSuccess?.();
    updateAffWlRecord();
  };

  const { providerPropertyProfiles } = useProviderPropertyProfiles({
    propertyId,
    enabled: isNewScreeningOn,
  });

  return (
    <div className="tab-pane active" id="1a">
      {leaseExecuted && (
        <RowWarning>
          <div className="highlight-details">
            <FormattedMessage {...messages.leaseLockedExecuted} />
          </div>
        </RowWarning>
      )}
      {leaseSentToPortal && (
        <RowWarning>
          <div className="highlight-details">
            <FormattedMessage {...messages.leaseLockedSentToPortal} />
          </div>
        </RowWarning>
      )}
      {!leaseAppRevampFullTab && (
        <LeaseApplication
          intl={intl}
          currentRecord={currentRecord}
          applicationDecisionStatus={applicationDecisionStatus}
          applicationStatuses={applicationStatuses}
          handleNotesChange={handleNotesChange}
          handleStatusChange={statusChangeHandler}
          checkUserPermissions={checkUserPermissions}
          leaseExecuted={leaseExecuted}
          leaseSentToPortal={leaseSentToPortal}
          applicationStatusId={applicationStatusId}
          headOfHouseholdId={headOfHouseholdId}
          applicationId={applicationId}
          modalProps={modalProps}
          affWlRecord={affWlRecord}
          propertyDetails={propertyDetails[0]}
          floorPlans={floorPlans}
          refreshApplication={refreshApplication}
        />
      )}
      {leaseAppRevampFullTab && (
        <>
          <LeaseApplicationStatusTracker
            application={currentRecord}
            providerPropertyProfiles={providerPropertyProfiles ?? []}
            householdId={householdId}
            handleNotesChange={handleNotesChange}
            propertyId={propertyId}
            organizationId={organizationId}
            property={selectedProperty}
            checkUserPermissions={checkUserPermissions}
            applicationUpdating={applicationUpdating}
            applicationStatusChangeHandler={applicationStatusChangeHandler}
            applicationStatuses={applicationStatuses}
            handleStatusChange={statusChangeHandler}
            waitlistModalProps={modalProps}
            leaseExecuted={leaseExecuted}
            leaseSentToPortal={leaseSentToPortal}
            headOfHouseholdId={headOfHouseholdId}
            affordableWaitlistRecord={affWlRecord}
            applicationStatusId={applicationStatusId}
            floorPlans={floorPlans}
            refreshApplication={refreshApplication}
          />
          <HouseholdApplicationsDrawer
            applicants={currentRecord?.applicants ?? []}
            handleEditApplicationForm={handleEditApplicationForm}
            propertyId={propertyId}
            organizationId={organizationId}
            applicationDecisionStatus={applicationDecisionStatus}
            handleMultipleChecklistOptionsChange={
              handleMultipleChecklistOptionsChange
            }
            affordaPortaV1={affordaPortaV1}
            mainCustomerId={currentRecord?.householdInfo?.mainCustomerId}
            application={currentRecord}
            selectedProperty={selectedProperty}
            currentUserId={currentUser?.user?.id}
            onResetSuccess={() => {
              // Refresh the application data after reset
              if (onResetSuccess) {
                onResetSuccess();
              }
            }}
          />
        </>
      )}
      {isNewScreeningOn ? (
        <CreditCriminalStatusScreening
          providerPropertyProfiles={providerPropertyProfiles ?? []}
          householdId={householdId}
          applicationId={applicationId}
          isApplicationCanceledOrDenied={isApplicationCanceledOrDenied}
          applicationStatus={applicationStatus?.text}
          intl={intl}
          adverseActionDeadlineDays={adverseActionDeadlineDays}
          screeningLetterTypes={screeningLetterTypes}
          organizationId={organizationId}
          propertyId={propertyId}
          promptToaster={promptToaster}
          isResident={false}
        />
      ) : (
        <CreditCriminalStatus
          intl={intl}
          applicationId={applicationId}
          screening={screening}
          onResubmitCallback={submitScreeningApplication}
          refreshActivityTable={refreshActivityTable}
          saveScreeningResults={saveScreeningResults}
          generateAdverseActionLetter={generateAdverseActionLetter}
          screeningLetterTypes={screeningLetterTypes}
          currentUser={currentUser}
          isResident={false}
          promptToaster={promptToaster}
          isTransUnionActive={isTransUnionActive}
          transunionOffLeaseAppOff={transunionOffLeaseAppOff}
          adverseActionDeadlineDays={adverseActionDeadlineDays}
        />
      )}
      {theWorkNumber && <TheWorkNumber {...theWorkNumberProps} />}
      {!leaseAppRevampFullTab && (
        <>
          <ApplicationChecklistCompletionStatus
            isTransUnionActive={isTransUnionActive}
            applicationChecklistUpdate={applicationChecklistUpdate}
          />
          <ApplicationChecklist
            {...{
              intl,
              currentRecord,
              applicationDecisionStatus,
              handleChecklistOptionChange,
              handleEditApplicationForm,
              leaseExecuted,
              leaseSentToPortal,
              affordaPortaV1,
            }}
          />
        </>
      )}
    </div>
  );
};

/**
 * In order to remove this reduxForm wrapper all redux-form <Fields/> must be removed from
 * src/containers/ApplicationProfile/LeaseApplicationTab.
 * @notes If the wrapper is removed in the future, consider the following for this component:
 * - double check data sent to api call `waitlistActions.updateWaitlist(applicationId,localWaitlistInfo)`
 * - double check unit tests that might fail due to reduxForm removal
 */
export default reduxForm({
  form: 'LeaseApplicationTab',
  enableReinitialize: true,
})(LeaseApplicationTab);
